import React, { useEffect, useState } from 'react';
import { Formik } from "formik";
import { setDisabled } from "../../../store/reducers/UserSlice";
import { getCardsInfo, getPhoneListThunk, sendCardRequest } from "../../../store/reducers/ActionCreators";
import s from "./VirtualCardRequest.module.css";
import CustomDropdown from "../../ui/CustomDropdown/CustomDropdown";
import MyInput from "../../ui/MyInput/MyInput";
import TransparentButton from "../../ui/TransparentButton/TransparentButton";
import Icon from "@ant-design/icons";
import MyBtn from "../../ui/MyBtn/MyBtn";
import CustomModal from "../../ui/CustomModal/CustomModal";
import { NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import * as yup from "yup";
import { ReactComponent as Back } from "../../../assets/images/personalAccount/back.svg";
import { CARD_LIMITS, EUR_CARD_COUNTRIES } from "../../../constants/CardConstants";
import { countries } from "../../../constants/StaticData";
import CustomDropdownForAccounts from "../../ui/CustomDropdownForAccounts/CustomDropdownForAccounts";
import { PrepareDataForCardRequest } from "./FormHelpers/FormHelpers";
import { Popover } from "antd";
import info from "../../../assets/images/personalAccount/information.svg";

type PropsType = {
    onCancelClick: () => void; 
    onSuccessClick: () => void;
    isModal?: boolean;
    cardStats?: {
        totalCards: number;
        eurCards: number;
        usdCards: number;
        totalBalance: string;
    };
};

interface Account {
    balance: number;
    currency: string;
    number: string;
    status: string;
    [key: string]: any;
}

const VirtualCardRequest = (props: PropsType) => {

    const {onCancelClick, isModal = false, cardStats} = props;
    const handleSuccessClick = () => {
        props.onSuccessClick();
    };

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);

    const dispatch = useAppDispatch();
    const token = useAppSelector(shippingCountyState => shippingCountyState.userReducer.user.userToken)
    const phoneList = useAppSelector(state => state.userReducer.user.phoneList);
    const accountsList = useAppSelector(state => state.userReducer.accountsList.accounts);
    const userInfo = useAppSelector(state => state.userReducer.user);
    const userCards: any[any] = useAppSelector(store => store.userReducer.cardInfo)
    const userCountryCode = useAppSelector(state => state.userReducer.user.userData.country);

    const getUserCountryName = (countryCode: string | null): string | null => {
        if (!countryCode) return null;
        const country = countries.find((c: any) => 
            (typeof c === 'string' ? c : c.value) === countryCode
        );
        return typeof country === 'string' ? country : ((country as any)?.name || null);
    };

    const userCountry = getUserCountryName(userCountryCode);


    useEffect(() => {
        dispatch(getPhoneListThunk(token));
    }, [dispatch, token]);

    useEffect(() => {
        let eurCardsCount = 0;
        let usdCardsCount = 0;
        
        if (cardStats) {
            eurCardsCount = cardStats.eurCards;
            usdCardsCount = cardStats.usdCards;
        } else {
            const cardInfo = Array.isArray(userCards) ? userCards : (userCards ? [userCards] : []);
            
            const eurCards = cardInfo.filter(card => 
                card.currency === 2 || card.currency === 'EUR'
            );
            const usdCards = cardInfo.filter(card => 
                card.currency === 1 || card.currency === 'USD'
            );
            
            eurCardsCount = eurCards.length;
            usdCardsCount = usdCards.length;
        }
        
        const canAddEurCard = eurCardsCount < CARD_LIMITS.EUR;
        const canAddUsdCard = usdCardsCount < CARD_LIMITS.USD;
        
        const isEurEligible = userCountry && EUR_CARD_COUNTRIES.includes(userCountry);
        
        const availableCurrencyList = [];
        
        if (canAddEurCard && isEurEligible) {
            availableCurrencyList.push('EUR');
        }
        if (canAddUsdCard) {
            availableCurrencyList.push('USD');
        }
        
        setAvailableCurrencies(availableCurrencyList);

        if (availableCurrencyList.length === 1) {
            setSelectedCurrency(availableCurrencyList[0]);
        } else {
            setSelectedCurrency('');
        }
    }, [userCards, cardStats, userCountry]);


    const phoneNumbers = phoneList?.filter(phone => phone.isVerify)?.map(phone => `+${phone.phoneNumber}`) || [];
    
    const getFilteredAccountsByCurrency = (currency: string) => {
        const filterByCurrency = (account: Account) => {
            if (currency === 'EUR') return account.currency === 'EUR' || account.currency === 'EUR-CARD';
            if (currency === 'USD') return account.currency === 'USD' || account.currency === 'USD-CARD';
            return account.currency === currency;
        };

        return accountsList.filter((account: Account) => filterByCurrency(account));
    };

    const filteredAccounts = getFilteredAccountsByCurrency(selectedCurrency);

    const getFilteredAccounts = () => {
        return getFilteredAccountsByCurrency(availableCurrencies[0]);
    }

    const validationSchema = yup.object({
        currency: selectedCurrency ? yup.string().notRequired() : yup.string().required('Required field'),
        email: yup.string().required('Required field').email("invalid format"),
        phone: phoneNumbers.length === 0 ? yup.string().notRequired() : yup.string().required('Required field'),
        shippingAddress: yup.string().required('Required field'),
        shippingCountry: yup.string().required('Required field'),
        shippingCity: yup.string().required('Required field'),
        shippingPostCode: yup.string().required('Required field'),
        payerAccount: yup.string().required('Required field'),
        transferCurrency: selectedCurrency ? yup.string().optional() : yup.string().required('Required field'),
        deliveryMethod: yup.string().required('Required field'),
    });



    return (
        <>
            <CustomModal setIsModalOpen={setIsSuccessModalOpen} isModalOpen={isSuccessModalOpen}>
                <div>
                    <div className={s.popup_title}>Success!</div>
                    <span className={s.popup_subtitle}> Your request has been sent.</span>
                    <div className={s.popup_button_wrapper}>
                        <div className={s.btn_block}>
                            <MyBtn title={"Ok"} onClick={handleSuccessClick} />
                        </div>
                    </div>
                </div>
            </CustomModal>

            <CustomModal setIsModalOpen={setIsErrorModalOpen} isModalOpen={isErrorModalOpen} type={"error"}>
                <div className={s.popup_title}>
                    Oops!
                </div>
                <div className={s.popup_subtitle}>
                    Something went wrong. Please try again later.
                </div>
                <div className={s.popup_button_wrapper}>
                    <div className={s.btn_block}>
                        <MyBtn buttonType={"error"} title={"Ok"} onClick={() => setIsErrorModalOpen(false)} />
                    </div>
                </div>
            </CustomModal>

            <div className={isModal ? s.modalWrapper : s.wrapper}>
                {!isModal && <div className={s.title}>Card Request</div>}
                <Formik
                    initialValues={{
                        currency: availableCurrencies?.length === 1 ? availableCurrencies[0] : selectedCurrency,
                        email: userInfo.userData.email ?? '',
                        phone: '',
                        shippingAddress: '',
                        shippingCountry: '',
                        shippingCity: '',
                        shippingCountyState: '',
                        shippingPostCode: '',
                        payerAccount: '',
                        transferCurrency: availableCurrencies?.length === 1 ? availableCurrencies[0] : selectedCurrency,
                        deliveryMethod: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                        let tempData = {};
                        if(availableCurrencies?.length === 1) tempData = { ...values, currency: availableCurrencies[0], transferCurrency: availableCurrencies[0], amount: '0' }
                        else tempData = { ...values, currency: selectedCurrency, amount: '0' };

                        const preparedData = PrepareDataForCardRequest(tempData);
                        dispatch(setDisabled(true));
                        
                        try {
                            const result = await dispatch(sendCardRequest(token, preparedData));
                            
                            // Check if we got a successful response with data
                            if (result && result.data) {
                                await dispatch(getCardsInfo(token));
                                setIsSuccessModalOpen(true);
                            } else {
                                // If no data or result is undefined, treat as error
                                console.error('Card request failed - no valid response:', result);
                                setIsErrorModalOpen(true);
                            }
                        } catch (error) {
                            console.error('Card request failed with error:', error);
                            setIsErrorModalOpen(true);
                        } finally {
                            dispatch(setDisabled(false));
                        }
                    }}>

                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleSubmit
                    }) => (
                        <form onSubmit={handleSubmit}>

                            
                            <div className={s.formTitle}>
                                Card Owner Details
                            </div>
                            <div className={isModal ? s.modalRow : s.row}>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Phone Number (Verified)
                                    </span>
                                    <CustomDropdown
                                        items={phoneNumbers}
                                        id={"phone"}
                                        name={"phone"}
                                        isError={errors.phone}
                                        touched={touched.phone}
                                        disable={phoneNumbers.length === 0}
                                        placeholder={phoneNumbers.length === 0 ? "No verified phone numbers available" : "Select verified phone number"}
                                    />
                                    {errors.phone && touched.phone && <div className={s.error}>{errors.phone}</div>}
                                    {phoneNumbers.length === 0 && (
                                        <div className={s.warningText} style={{ color: '#ff6b35', fontSize: '12px', marginTop: '5px' }}>
                                            You need to verify a phone number before requesting a card. 
                                            <NavLink to="/personal_account/personal/settings" style={{ color: '#1890ff', marginLeft: '5px' }}>
                                                Go to Settings
                                            </NavLink>
                                        </div>
                                    )}
                                </div>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Email Address
                                    </span>
                                    <MyInput
                                        name={"email"}
                                        id={"email"}
                                        value={values.email}
                                        touched={touched.email}
                                        onChange={handleChange}
                                        isError={errors.email}
                                        width={isModal ? "100%" : "288px"}
                                    />
                                    {errors.email && <div className={s.error}>{errors.email}</div>}
                                </div>
                            </div>

                            <div className={s.formTitle}>
                                Card Configuration
                            </div>

                            <div className={isModal ? s.modalRow : s.row}>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Card Currency
                                    </span>
                                    <CustomDropdown
                                        items={availableCurrencies}
                                        id="transferCurrency"
                                        name="transferCurrency"
                                        touched={touched.transferCurrency}
                                        disable={availableCurrencies?.length === 1}
                                        placeholder={availableCurrencies?.length === 1 ? availableCurrencies[0] : ''}
                                        onChange={(val) => val && setSelectedCurrency(val)}
                                        isError={errors.transferCurrency}
                                    />
                                </div>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Funding Account
                                        <Popover content={"Accounts with balance equal to 0 can't be chosen."}
                                            color={'#EEEFEF'}
                                            overlayInnerStyle={{
                                                fontFamily: 'Inter',
                                                width: '280px',
                                                padding: '13px 16px'
                                            }}>
                                            <img src={info} alt="" className={s.infoIcon} />
                                        </Popover>
                                    </span>
                                    <CustomDropdownForAccounts
                                        items={availableCurrencies?.length === 1 ? getFilteredAccounts() : filteredAccounts}
                                        placeholder={"Please select the account"}
                                        id="payerAccount"
                                        name="payerAccount"
                                        isError={errors.payerAccount}
                                        touched={touched.payerAccount}
                                        disableZeroBalance={true}
                                    />
                                    {errors.payerAccount && touched.payerAccount &&
                                        <div className={s.error}>{errors.payerAccount}</div>}
                                </div>
                            </div>

                            <div className={s.formTitle}>
                                Delivery Address
                            </div>
                            <div className={s.warningText}>Please verify the delivery address before requesting the card, as it is non-changeable.</div>

                            <div className={isModal ? s.modalRow : s.row}>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Address
                                    </span>
                                    <MyInput
                                        name={"shippingAddress"}
                                        id={"shippingAddress"}
                                        value={values.shippingAddress}
                                        touched={touched.shippingAddress}
                                        onChange={handleChange}
                                        isError={errors.shippingAddress}
                                        placeholder={"Street/Building/Flat"}
                                        width={isModal ? "100%" : "288px"}
                                    />
                                    {errors.shippingAddress && <div className={s.error}>{errors.shippingAddress}</div>}
                                </div>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Country
                                    </span>
                                    <CustomDropdown
                                        items={countries.map((country: any) => typeof country === 'string' ? country : country.name)}
                                        placeholder={"Country"}
                                        id="shippingCountry"
                                        name="shippingCountry"
                                        isError={errors.shippingCountry}
                                        touched={touched.shippingCountry}
                                    />
                                    {errors.shippingCountry && <div className={s.error}>{errors.shippingCountry}</div>}
                                </div>
                            </div>

                            <div className={isModal ? s.modalRow : s.row}>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        City
                                    </span>
                                    <MyInput
                                        name={"shippingCity"}
                                        id={"shippingCity"}
                                        value={values.shippingCity}
                                        touched={touched.shippingCity}
                                        onChange={handleChange}
                                        isError={errors.shippingCity}
                                        width={isModal ? "100%" : "288px"}
                                    />
                                    {errors.shippingCity && <div className={s.error}>{errors.shippingCity}</div>}
                                </div>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        State
                                    </span>
                                    <MyInput
                                        name={"shippingCountyState"}
                                        id={"shippingCountyState"}
                                        value={values.shippingCountyState}
                                        touched={touched.shippingCountyState}
                                        onChange={handleChange}
                                        isError={errors.shippingCountyState}
                                        width={isModal ? "100%" : "288px"}
                                    />
                                    {errors.shippingCountyState && <div className={s.error}>{errors.shippingCountyState}</div>}
                                </div>
                            </div>

                            <div className={isModal ? s.modalRow : s.row}>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Post Code
                                    </span>
                                    <MyInput
                                        name={"shippingPostCode"}
                                        id={"shippingPostCode"}
                                        value={values.shippingPostCode}
                                        touched={touched.shippingPostCode}
                                        onChange={handleChange}
                                        isError={errors.shippingPostCode}
                                        width={isModal ? "100%" : "288px"}
                                    />
                                    {errors.shippingPostCode && <div className={s.error}>{errors.shippingPostCode}</div>}
                                </div>
                                <div className={isModal ? s.modalInputBlock : s.inputBlock}>
                                    <span>
                                        <span className={s.required}>*</span>
                                        Delivery Method
                                    </span>
                                    <CustomDropdown items={['Standard', 'Registered']} id={"deliveryMethod"}
                                        name={"deliveryMethod"} isError={errors.deliveryMethod}
                                        touched={touched.deliveryMethod}
                                        placeholder={"Select shipping method"} />
                                    {errors.deliveryMethod && touched.deliveryMethod &&
                                        <div className={s.error}>{errors.deliveryMethod}</div>}
                                </div>
                            </div>

                            <div className={s.infoRow}>
                                <span className={s.required}>*</span> Required fields. <br />
                                <div className={s.formSpan}>
                                    Please check your profile information. If needed, please
                                    <NavLink to={`/personal_account/personal/settings`}>
                                        <div className={s.link}> Edit Your Profile.
                                        </div>
                                    </NavLink>
                                </div>
                            </div>

                            <div className={isModal ? s.modalButtonsWrapper : s.buttonsWrapper}>
                                <MyBtn
                                    large
                                    style={{ width: 180 }}
                                    title={"Agree"}
                                    type={"submit"}
                                    onClick={() => console.log(errors)}
                                />
                                <TransparentButton
                                    title={"Cancel"}
                                    style={{ width: 180 }}
                                    large
                                    icon={<Icon component={() => <Back />} rev={undefined} />}
                                    onClick={onCancelClick}
                                    type={'button'}
                                />
                            </div>

                        </form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default VirtualCardRequest;